/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { ApprovalCommandDtov1 } from '../models/approval-command-dtov-1';
import { ApprovalQueryDtov1 } from '../models/approval-query-dtov-1';
import { ApprovalUpdateCommandDtov1 } from '../models/approval-update-command-dtov-1';
import { UserQueryDto } from '../models/user-query-dto';


/**
 * API for managing V1 approval matrix (request-based approvals)
 */
@Injectable({
  providedIn: 'root',
})
export class ApprovalV1Service extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation updateApprovalV1
   */
  static readonly UpdateApprovalV1Path = '/v1/approvals/{approvalId}';

  /**
   * Update existing approval rule conditions.
   *
   * Updates the conditions of an existing approval rule. Stage and userId cannot be changed - only conditions can be updated.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateApprovalV1()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateApprovalV1$Response(params: {
    approvalId: string;

    /**
     * Approval rule update data
     */
    body: ApprovalUpdateCommandDtov1
  },
  context?: HttpContext

): Observable<StrictHttpResponse<ApprovalQueryDtov1>> {

    const rb = new RequestBuilder(this.rootUrl, ApprovalV1Service.UpdateApprovalV1Path, 'put');
    if (params) {
      rb.path('approvalId', params.approvalId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ApprovalQueryDtov1>;
      })
    );
  }

  /**
   * Update existing approval rule conditions.
   *
   * Updates the conditions of an existing approval rule. Stage and userId cannot be changed - only conditions can be updated.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateApprovalV1$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateApprovalV1(params: {
    approvalId: string;

    /**
     * Approval rule update data
     */
    body: ApprovalUpdateCommandDtov1
  },
  context?: HttpContext

): Observable<ApprovalQueryDtov1> {

    return this.updateApprovalV1$Response(params,context).pipe(
      map((r: StrictHttpResponse<ApprovalQueryDtov1>) => r.body as ApprovalQueryDtov1)
    );
  }

  /**
   * Path part for operation deleteApprovalV1
   */
  static readonly DeleteApprovalV1Path = '/v1/approvals/{approvalId}';

  /**
   * Delete an assignment of the user to the approval stages.
   *
   * Deletes the selected approval matrix configuration.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteApprovalV1()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteApprovalV1$Response(params: {
    approvalId: string;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ApprovalV1Service.DeleteApprovalV1Path, 'delete');
    if (params) {
      rb.path('approvalId', params.approvalId, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Delete an assignment of the user to the approval stages.
   *
   * Deletes the selected approval matrix configuration.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteApprovalV1$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteApprovalV1(params: {
    approvalId: string;
  },
  context?: HttpContext

): Observable<void> {

    return this.deleteApprovalV1$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation listApprovalsV1
   */
  static readonly ListApprovalsV1Path = '/v1/approvals';

  /**
   * List available approvals for browsing".
   *
   * Provides list of approvals for request-based approvals. Approvals are returned as unordered list.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listApprovalsV1()` instead.
   *
   * This method doesn't expect any request body.
   */
  listApprovalsV1$Response(params?: {
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Array<ApprovalQueryDtov1>>> {

    const rb = new RequestBuilder(this.rootUrl, ApprovalV1Service.ListApprovalsV1Path, 'get');
    if (params) {
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<ApprovalQueryDtov1>>;
      })
    );
  }

  /**
   * List available approvals for browsing".
   *
   * Provides list of approvals for request-based approvals. Approvals are returned as unordered list.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listApprovalsV1$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listApprovalsV1(params?: {
  },
  context?: HttpContext

): Observable<Array<ApprovalQueryDtov1>> {

    return this.listApprovalsV1$Response(params,context).pipe(
      map((r: StrictHttpResponse<Array<ApprovalQueryDtov1>>) => r.body as Array<ApprovalQueryDtov1>)
    );
  }

  /**
   * Path part for operation createApprovalV1
   */
  static readonly CreateApprovalV1Path = '/v1/approvals';

  /**
   * Create new approval rule.
   *
   * Creates a new approval rule for request-based approvals. The formId field allows targeting specific form templates.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createApprovalV1()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createApprovalV1$Response(params: {

    /**
     * Approval rule to be created
     */
    body: ApprovalCommandDtov1
  },
  context?: HttpContext

): Observable<StrictHttpResponse<ApprovalQueryDtov1>> {

    const rb = new RequestBuilder(this.rootUrl, ApprovalV1Service.CreateApprovalV1Path, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ApprovalQueryDtov1>;
      })
    );
  }

  /**
   * Create new approval rule.
   *
   * Creates a new approval rule for request-based approvals. The formId field allows targeting specific form templates.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createApprovalV1$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createApprovalV1(params: {

    /**
     * Approval rule to be created
     */
    body: ApprovalCommandDtov1
  },
  context?: HttpContext

): Observable<ApprovalQueryDtov1> {

    return this.createApprovalV1$Response(params,context).pipe(
      map((r: StrictHttpResponse<ApprovalQueryDtov1>) => r.body as ApprovalQueryDtov1)
    );
  }

  /**
   * Path part for operation listApproverUsersV1
   */
  static readonly ListApproverUsersV1Path = '/v1/approvals/approvers';

  /**
   * List available approver users for browsing.
   *
   * Provides list of approver users. Approvers are returned as unordered list.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listApproverUsersV1()` instead.
   *
   * This method doesn't expect any request body.
   */
  listApproverUsersV1$Response(params?: {
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Array<UserQueryDto>>> {

    const rb = new RequestBuilder(this.rootUrl, ApprovalV1Service.ListApproverUsersV1Path, 'get');
    if (params) {
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<UserQueryDto>>;
      })
    );
  }

  /**
   * List available approver users for browsing.
   *
   * Provides list of approver users. Approvers are returned as unordered list.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listApproverUsersV1$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listApproverUsersV1(params?: {
  },
  context?: HttpContext

): Observable<Array<UserQueryDto>> {

    return this.listApproverUsersV1$Response(params,context).pipe(
      map((r: StrictHttpResponse<Array<UserQueryDto>>) => r.body as Array<UserQueryDto>)
    );
  }

}
